import React from 'react';
import { useNotificationPreferences, useUpdateNotificationPreference } from '../../hooks/useNotificationPreferences';
import { NotificationPreference } from '../../types';
import { Card } from '../ui/Card';
import { Spinner } from '../ui/Spinner';
import { Check, Info, Bell, ShieldAlert } from 'lucide-react';
import { cn } from '../../lib/utils';
import debounce from 'lodash.debounce';
import { motion, AnimatePresence } from 'motion/react';

interface NotificationPreferencesProps {
  nodeId?: string;
}

const TogglePill: React.FC<{
  label: string;
  isEnabled: boolean;
  isLocked: boolean;
  onClick: () => void;
}> = ({ label, isEnabled, isLocked, onClick }) => {
  return (
    <button
      onClick={isLocked ? undefined : onClick}
      disabled={isLocked}
      className={cn(
        "px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all duration-200 border",
        isEnabled 
          ? "bg-navy-900 text-white border-navy-900" 
          : "bg-white text-gray-500 border-gray-300 hover:border-blue-400 hover:text-blue-600 dark:bg-slate-900",
        isLocked && "cursor-not-allowed opacity-80 hover:border-gray-300 hover:text-gray-500"
      )}
      title={isLocked ? "Disabled by organization/system lock" : undefined}
    >
      {label}
    </button>
  );
};

export const NotificationPreferences: React.FC<NotificationPreferencesProps> = ({ nodeId }) => {
  const { data: response, isLoading } = useNotificationPreferences();
  const updateMutation = useUpdateNotificationPreference();
  const [showSaved, setShowSaved] = React.useState(false);

  // Determine current preferences based on whether nodeId is passed and has specific overrides
  const preferences = React.useMemo(() => {
    if (!response) return [];
    if (nodeId && response.nodeSpecific?.[nodeId]) {
      return response.nodeSpecific[nodeId];
    }
    return response.global;
  }, [response, nodeId]);

  // Debounced update function
  const debouncedUpdate = React.useMemo(
    () =>
      debounce((pref: Partial<NotificationPreference> & { type: string }) => {
        const currentPrefs = preferences || [];
        
        // Find existing to replace
        const nextTargetPrefs = currentPrefs.map(p => p.type === pref.type ? { ...p, ...pref } : p);
        if (!currentPrefs.find(p => p.type === pref.type)) {
          // If preference object does not exist yet (should not happen with good backend)
          nextTargetPrefs.push(pref as NotificationPreference);
        }

        const data: any = {};
        if (nodeId) {
          data.nodeId = nodeId;
          data.nodePreferences = nextTargetPrefs;
        } else {
          data.global = nextTargetPrefs;
        }

        updateMutation.mutate(data, {
          onSuccess: () => {
            setShowSaved(true);
            setTimeout(() => setShowSaved(false), 2000);
          }
        });
      }, 500),
    [updateMutation, preferences, nodeId]
  );

  const handleToggle = (type: string, channel: 'app' | 'email' | 'sms' | 'push', currentValue: boolean) => {
    debouncedUpdate({ type, [channel]: !currentValue });
  };

  if (isLoading) return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Notification Settings</h3>
          <p className="text-xs text-gray-500 mt-1">Configure how and where you receive updates.</p>
        </div>
        <AnimatePresence>
          {showSaved && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-1.5 text-teal-600 text-xs font-semibold bg-teal-50 px-3 py-1.5 rounded-full border border-teal-100"
            >
              <Check size={14} strokeWidth={3} />
              Saved
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="divide-y divide-gray-100">
          {preferences?.map((pref) => {
            const lockedList = Array.isArray(pref.locked) ? pref.locked : [];
            const isAnyLocked = lockedList.length > 0;
            return (
            <div key={pref.type} className="flex flex-col md:flex-row md:items-center justify-between p-4 hover:bg-gray-50 transition-colors gap-4 dark:bg-slate-950">
              <div className="flex items-start gap-3">
                <div className={cn(
                  "p-2 rounded-lg mt-0.5",
                  isAnyLocked ? "bg-amber-50 text-amber-600" : "bg-blue-50 text-blue-600"
                )}>
                  {isAnyLocked ? <ShieldAlert size={16} /> : <Bell size={16} />}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{pref.label}</p>
                  {isAnyLocked && (
                    <div className="flex items-center gap-1 mt-1 text-[10px] text-amber-700 font-medium">
                      <Info size={10} />
                      Locked by organization/system
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <TogglePill 
                  label="App" 
                  isEnabled={pref.app} 
                  isLocked={lockedList.includes('app')} 
                  onClick={() => handleToggle(pref.type, 'app', pref.app)}
                />
                <TogglePill 
                  label="Email" 
                  isEnabled={pref.email} 
                  isLocked={lockedList.includes('email')} 
                  onClick={() => handleToggle(pref.type, 'email', pref.email)}
                />
                <TogglePill 
                  label="SMS" 
                  isEnabled={pref.sms} 
                  isLocked={lockedList.includes('sms')} 
                  onClick={() => handleToggle(pref.type, 'sms', pref.sms)}
                />
                <TogglePill 
                  label="Push" 
                  isEnabled={pref.push} 
                  isLocked={lockedList.includes('push')} 
                  onClick={() => handleToggle(pref.type, 'push', pref.push)}
                />
              </div>
            </div>
          );
        })}
        </div>
      </Card>

      <div className="flex items-center gap-2 p-4 bg-gray-100 rounded-xl border border-gray-200 dark:bg-slate-800">
        <Info size={16} className="text-gray-400 flex-shrink-0" />
        <p className="text-[10px] text-gray-500 leading-relaxed">
          Some notifications are mandatory for organizational safety and cannot be disabled. 
          SMS charges may apply depending on your carrier.
        </p>
      </div>
    </div>
  );
};
